import {
  Box,
  Button,
  Checkbox,
  Dropdown,
  DropdownOption,
  Grid,
  GridColumn,
  Pagination,
  RadioButton,
  RadioGroup,
  Tabs,
  Text
} from "@modernatx-internal/component-library";
import { usePathname, useSearchParams } from "next/navigation";
import React from "react";

import { useParseLocalizedPathname } from "@/hooks/useParseLocalizedPathname";

import { useResourceList } from "../hooks/useResourceList";
import { ContentComponentProps, ResourceListContent } from "../types";
import { PageImage } from "./PageImage";

enum SortOption {
  AToZ = "a-to-z",
  MostPopular = "most-popular",
  Newest = "newest",
  ZToA = "z-to-a"
}

enum TagOption {
  Adults18To64 = "adults-18-to-64",
  Adults65AndUp = "adults-65-and-up",
  Comorbid = "comorbid",
  Pediatric = "pediatric"
}

interface ResourceListFiltersProps {
  filtersSelected: string[];
  onFilterChange: (filterOptions: string[]) => void;
  resultCount: number;
  sortSelected: string | null;
  sortSet: (sortOption: string) => void;
  patientTypeOptions: DropdownOption[];
  resourceTypeOptions: DropdownOption[];
}

export const ResourceListFilters: React.FC<ResourceListFiltersProps> = ({
  filtersSelected,
  resultCount,
  sortSelected,
  patientTypeOptions,
  resourceTypeOptions,
  onFilterChange,
  sortSet
}) => {
  const handleSelectSortOption = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const selectedOptionNext = event.target.value;
      sortSet(selectedOptionNext ? selectedOptionNext : SortOption.Newest);
    },
    [sortSet]
  );

  return (
    <Box
      sx={{
        borderColor: "stroke-neutral-emphasis",
        borderWidth: "1px",
        borderStyle: "solid",
        borderRadius: "radius-2",
        p: 4,
        display: "flex",
        flexDirection: "column",
        gap: 4
      }}
    >
      <Text as="h1" sx={{ fontWeight: "font-weight-medium", fontSize: "font-size-5" }}>
        Filter resources
      </Text>

      {patientTypeOptions.length > 0 && (
        <>
          <Text
            as="h2"
            sx={{
              paddingTop: 4,
              paddingLeft: 3,
              fontWeight: "font-weight-medium",
              fontSize: "font-size-4"
            }}
          >
            Patient Type
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 3 }}>
            {patientTypeOptions.map((option) => (
              <Checkbox
                key={option.value}
                checked={filtersSelected.includes(String(option.value))}
                label={option.label}
                onChange={() => {
                  const filterValue = String(option.value);
                  const newFilters = filtersSelected.includes(filterValue)
                    ? filtersSelected.filter((f) => f !== filterValue)
                    : [...filtersSelected, filterValue];
                  onFilterChange(newFilters);
                }}
              />
            ))}
          </Box>
        </>
      )}

      {resourceTypeOptions.length > 0 && (
        <>
          <Text
            as="h2"
            sx={{
              paddingTop: 4,
              paddingLeft: 3,
              fontWeight: "font-weight-medium",
              fontSize: "font-size-4"
            }}
          >
            Resource Type
          </Text>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 4, paddingLeft: 3 }}>
            {resourceTypeOptions.map((option) => (
              <Checkbox
                key={option.value}
                checked={filtersSelected.includes(String(option.value))}
                label={option.label}
                onChange={() => {
                  const filterValue = String(option.value);
                  const newFilters = filtersSelected.includes(filterValue)
                    ? filtersSelected.filter((f) => f !== filterValue)
                    : [...filtersSelected, filterValue];
                  onFilterChange(newFilters);
                }}
              />
            ))}
          </Box>
        </>
      )}

      <RadioGroup
        label="Sort By"
        value={sortSelected ?? SortOption.Newest}
        onChange={handleSelectSortOption}
      >
        <RadioButton value={SortOption.Newest} label="Newest" />
        <RadioButton value={SortOption.AToZ} label="A-Z" />
        <RadioButton value={SortOption.ZToA} label="Z-A" />
      </RadioGroup>
      <Text sx={{ color: "text-secondary", fontWeight: "font-weight-medium" }} as="p">
        {resultCount !== 1 ? `${resultCount} Results` : `${resultCount} Result`}
      </Text>
    </Box>
  );
};

export const ResourceList: React.FC<ContentComponentProps<ResourceListContent>> = ({ items }) => {
  const SCROLL_ID = "ResourcesTop";
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { getLocalizedPathnamesMatch } = useParseLocalizedPathname();
  const { filters, filtersSet, page, pageSet, sort, sortSet } = useResourceList();
  const itemsMax = 20;

  // Dynamic resource type options based on actual data
  const resourceTypeOptions: DropdownOption[] = React.useMemo(() => {
    const uniqueResourceTypes = new Set<string>();

    items.forEach((item) => {
      item.resources.forEach((resource) => {
        if (resource.resourceType) {
          uniqueResourceTypes.add(resource.resourceType);
        }
      });
    });

    const getResourceTypeLabel = (type: string): string => {
      switch (type) {
        case "video":
          return "Video";
        case "pdf":
          return "PDF";
        case "document":
          return "Document";
        default:
          return type.charAt(0).toUpperCase() + type.slice(1);
      }
    };

    return Array.from(uniqueResourceTypes)
      .sort()
      .map((type) => ({
        label: getResourceTypeLabel(type),
        value: type
      }));
  }, [items]);

  // Dynamic patient type options based on actual tags in the data
  const patientTypeOptions: DropdownOption[] = React.useMemo(() => {
    const uniqueTags = new Set<string>();

    items.forEach((item) => {
      item.tags.forEach((tag) => {
        uniqueTags.add(tag);
      });
    });

    const getTagLabel = (tag: string): string => {
      switch (tag) {
        case TagOption.Adults18To64:
          return "Adults (18-64)";
        case TagOption.Adults65AndUp:
          return "Adults (65+)";
        case TagOption.Comorbid:
          return "Comorbid";
        case TagOption.Pediatric:
          return "Pediatric";
        default:
          return tag.replace(/-/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
      }
    };

    return Array.from(uniqueTags)
      .sort()
      .map((tag) => ({
        label: getTagLabel(tag),
        value: tag
      }));
  }, [items]);

  const sortOptions: DropdownOption[] = [
    { label: "Newest", value: SortOption.Newest },
    { label: "A-Z", value: SortOption.AToZ },
    { label: "Z-A", value: SortOption.ZToA }
  ];

  const sortOption = sortOptions.find((so) => so.value === sort) || sortOptions[0]!;

  // Update filtering logic to handle both patient types and resource types
  const itemsFiltered = React.useMemo(() => {
    if (!filters.length) {
      return [...items];
    }

    return items.filter((item) => {
      // Check if item matches patient type filters (tags)
      const hasMatchingTag = item.tags.some((tag) => filters.includes(tag));

      // Check if item matches resource type filters
      const hasMatchingResourceType = item.resources.some((resource) =>
        filters.includes(resource.resourceType)
      );

      return hasMatchingTag || hasMatchingResourceType;
    });
  }, [filters, items]);

  const sortOptionValue = sortOption.value;
  const itemsSorted = React.useMemo(() => {
    return itemsFiltered.sort((prev, next) => {
      switch (sortOptionValue) {
        case SortOption.AToZ:
          return prev.title.localeCompare(next.title);
        case SortOption.Newest:
          return next.createdAt - prev.createdAt;
        case SortOption.ZToA:
          return next.title.localeCompare(prev.title);
        default:
          return 0;
      }
    });
  }, [itemsFiltered, sortOptionValue]);

  const pageCount = itemsSorted.length === 0 ? 0 : Math.ceil(itemsSorted.length / itemsMax);
  const pageStart = (page - 1) * itemsMax;
  const itemsPaged = itemsSorted.slice(pageStart, pageStart + itemsMax);
  const search = React.useMemo(() => {
    const params = new URLSearchParams(searchParams.toString());
    params.delete("page");
    const qs = params.toString();
    return qs ? `?${qs}` : "";
  }, [searchParams]);
  const tabItems = React.useMemo(() => {
    return [
      { href: `/pro/resources${search}`, id: "0", label: "For Your Practice" },
      {
        href: `/pro/resources/for-your-pharmacy${search}`,
        id: "2",
        label: "For Your Pharmacy"
      }
    ];
  }, [search]);

  const formatter = new Intl.DateTimeFormat("en-US", {
    month: "long",
    year: "numeric",
    timeZone: "UTC"
  });

  const handlePaginationChange = React.useCallback(
    (index: number) => {
      pageSet(index);

      const scrollToElement = document.getElementById(SCROLL_ID);

      if (scrollToElement) {
        scrollToElement.scrollIntoView();
      }
    },
    [SCROLL_ID, pageSet]
  );

  const handleSelectSortOption = React.useCallback(
    (selectedOptionNext: DropdownOption) => {
      sortSet(selectedOptionNext.value ? String(selectedOptionNext.value) : null);
    },
    [sortSet]
  );

  // Dynamic resource display helpers
  const getResourceLabel = React.useCallback((resourceType: string): string => {
    switch (resourceType) {
      case "video":
        return "Watch Video";
      case "pdf":
        return "Download PDF";
      case "document":
        return "Download Document";
      default:
        return "Download";
    }
  }, []);

  const getResourceIcon = React.useCallback(
    (resourceType: string): "play-circle" | "download-simple" => {
      switch (resourceType) {
        case "video":
          return "play-circle";
        case "pdf":
        case "document":
        default:
          return "download-simple";
      }
    },
    []
  );

  return (
    <Grid variant="uniform-12">
      <GridColumn size={[12, 12, 8]} push={[0, 0, 4]}>
        <Box sx={{ pb: 4 }} id={SCROLL_ID}>
          <Tabs
            items={tabItems}
            selectedTabId={
              tabItems.find((tabItem) => getLocalizedPathnamesMatch(tabItem.href, pathname))?.id
            }
          />
        </Box>
      </GridColumn>
      <GridColumn size={[12, 12, 4]} sx={{ display: ["none", null, "block"] }}>
        <ResourceListFilters
          filtersSelected={filters}
          sortSelected={sort}
          resultCount={itemsSorted.length}
          patientTypeOptions={patientTypeOptions}
          resourceTypeOptions={resourceTypeOptions}
          sortSet={sortSet}
          onFilterChange={filtersSet}
        />
      </GridColumn>
      <GridColumn size={[12, 12, 8]}>
        <Box>
          <Grid variant="uniform-12">
            <GridColumn
              size={[12, 12, 3]}
              sx={{ display: ["block", null, "none"], flexGrow: 0, pb: 8 }}
            >
              <Box
                sx={{
                  borderColor: "stroke-neutral-emphasis",
                  borderWidth: "1px",
                  borderStyle: "solid",
                  borderRadius: "radius-2",
                  p: 4,
                  display: "flex",
                  flexDirection: "column",
                  gap: 4
                }}
              >
                <Text as="h1" sx={{ fontWeight: "font-weight-medium", fontSize: "font-size-5" }}>
                  Filter resources
                </Text>

                {patientTypeOptions.length > 0 && (
                  <Dropdown
                    label="Patient Type"
                    dropdownOptions={patientTypeOptions}
                    multiSelect={true}
                    selectedOptions={patientTypeOptions.filter((option) =>
                      filters.includes(String(option.value))
                    )}
                    onSelectOptions={(selectedOptions: DropdownOption[]) => {
                      const patientTypeFilters = selectedOptions.map((opt) => String(opt.value));
                      const resourceTypeFilters = filters.filter((f) =>
                        resourceTypeOptions.some((rto) => String(rto.value) === f)
                      );
                      filtersSet([...patientTypeFilters, ...resourceTypeFilters]);
                    }}
                  />
                )}

                {resourceTypeOptions.length > 0 && (
                  <Dropdown
                    label="Resource Type"
                    dropdownOptions={resourceTypeOptions}
                    multiSelect={true}
                    selectedOptions={resourceTypeOptions.filter((option) =>
                      filters.includes(String(option.value))
                    )}
                    onSelectOptions={(selectedOptions: DropdownOption[]) => {
                      const resourceTypeFilters = selectedOptions.map((opt) => String(opt.value));
                      const patientTypeFilters = filters.filter((f) =>
                        patientTypeOptions.some((pto) => String(pto.value) === f)
                      );
                      filtersSet([...patientTypeFilters, ...resourceTypeFilters]);
                    }}
                  />
                )}

                <Dropdown
                  label="Sort by"
                  dropdownOptions={sortOptions}
                  selectedOption={sortOption}
                  onSelectOption={handleSelectSortOption}
                />
                <Text sx={{ color: "text-secondary", fontWeight: "font-weight-medium" }} as="p">
                  {itemsSorted.length !== 1
                    ? `${itemsSorted.length} Results`
                    : `${itemsSorted.length} Result`}
                </Text>
              </Box>
            </GridColumn>
          </Grid>
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", flex: 1, gap: 5 }}>
          {!itemsSorted.length && (
            <Text as="div" sx={{ textAlign: "center", pt: 4 }} variant="h5">
              No resources match the selections
            </Text>
          )}
          {itemsPaged.map((item, i) => (
            <Box
              key={i}
              sx={{
                backgroundColor: "surface-brand",
                borderRadius: "radius-2",
                display: "flex",
                flexDirection: ["column", "row", "row"],
                alignItems: ["stretch", "flex-start", "flex-start"],
                gap: [3, 8, 8],
                px: [6, 4, 4],
                py: [6, 4, 4]
              }}
            >
              <Box
                sx={{
                  width: 25,
                  flexShrink: 0,
                  alignSelf: ["flex-start", "auto", "auto"],
                  mb: [2, 0, 0]
                }}
              >
                <PageImage src={item.image} sx={{ width: "100%" }}>
                  {[]}
                </PageImage>
              </Box>
              <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 2 }}>
                <Text as="p" variant="caption" sx={{ fontStyle: "italic" }}>
                  Date Published: {formatter.format(new Date(item.createdAt))}
                </Text>

                <Text as="h3" variant="title-1-medium" sx={{ mt: [1, 0, 0] }}>
                  {item.title}
                </Text>

                <Text as="p" sx={{ mt: [1, 0, 0] }}>
                  {item.description}
                </Text>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: ["column", "row", "row"],
                    alignItems: ["flex-start", "center", "center"],
                    gap: [3, 4, 4],
                    mt: [2, 3, 3]
                  }}
                >
                  {item.resources.map((resource, k) => (
                    <React.Fragment key={k}>
                      <Button
                        href={resource.url}
                        icon={getResourceIcon(resource.resourceType)}
                        iconPosition="end"
                        variant="outline"
                        sx={{ alignSelf: ["flex-start", "auto", "auto"] }}
                      >
                        {getResourceLabel(resource.resourceType)}
                      </Button>

                      {resource.customizableFile && (
                        <Button
                          href={resource.customizableFile}
                          icon="download-simple"
                          iconPosition="end"
                          variant="outline"
                          sx={{ alignSelf: ["flex-start", "auto", "auto"] }}
                        >
                          Customizable File
                        </Button>
                      )}
                    </React.Fragment>
                  ))}
                </Box>
              </Box>
            </Box>
          ))}
        </Box>
        {itemsSorted.length > itemsMax && (
          <Box sx={{ pt: 8 }}>
            <Pagination count={pageCount} page={page} onPageChange={handlePaginationChange} />
          </Box>
        )}
      </GridColumn>
    </Grid>
  );
};
